import React from "react";
import { Navigate } from "react-router-dom";
import DesignSystem from "../pages/DesignSystem";
import LayoutPage from "../pages/Layout";
import Profile from "../pages/Profile";
import AdminLogin from "../pages/AdminLogin";
import Dashboard from "../pages/Dashboard";
import Signup from "../pages/Signup";
import Home from "../pages/Home";
import Compoz from "../pages/Compoz";
import PlayGround from "../pages/PlayGround";
import Top from "../pages/Top";
import Setting from "../pages/Setting";
import UudTools from "../pages/UudTools";
import Apps from "../pages/Apps";
import ConfirmEmailChange from "../pages/ConfirmEmailChange";
import ConfirmPasswordReset from "../pages/ConfirmPasswordReset";
import ConfirmVerification from "../pages/ConfirmVerification";
import SignupRequestVerify from "../pages/SignupRequestVerify";
import NotFound from "../pages/NotFound";
import AlertStyleLab from "../pages/AlertStyleLab";
import EventTemp from "../pages/camp/EventTemp";
import CampEventPrivacyPolicy from "../pages/camp/CampEventPrivacyPolicy";

// Import UT components
import Colorz from "../ut/Colorz";
import CompareImage from "../ut/CompareImage";
import Timer from "../ut/Timer";
import RandomColorz from "../ut/RandomColorz";


const pages = {
  DesignSystem,
  Compoz,
  PlayGround,
  Top,
  Signup,
  Setting,
  LayoutPage,
  Home,
  resos: Home,
  ideaz: Home,
  tools: Home,
  Profile,
  AdminLogin,
  Dashboard,
  UudTools,
  Apps,
  AlertStyleLab,
};

// UT Components
const utComponents = {
  Colorz,
  CompareImage,
  Timer,
  RandomColorz,
};

// - - - - - - - - - - - PAGES

const pageRoutes = Object.keys(pages)
  .filter((page) => page !== "Profile" && page !== "Top")
  .flatMap((page) => {
  const Component = pages[page];

  if (page === "Signup") {
    const signupElement = (
      <Component
        newPopup={() => console.log("New popup triggered!")}
        popups={[]}
        closePopup={() => console.log("Popup closed!")}
      />
    );
    return [
      { path: `/signup`, element: signupElement },
      { path: `/signup/:id/email/:email`, element: signupElement },
    ];
  }

  if (page === "resos") {
    return {
      path: `/resos`,
      element: <Home selectedMode="resos" />,
    };
  }

  if (page === "ideaz") {
    return {
      path: `/ideaz`,
      element: <Home selectedMode="ideaz" />,
    };
  }

  return {
    path: `/${page.toLowerCase()}`,
    element: <Component />,
  };
});

// - - - - - - - - - - - UI Components
const uiComponents = {
  // Add UI component mappings here if needed
};

const uiRoutes = Object.keys(uiComponents).map((component) => {
  const Component = uiComponents[component];
  return {
    path: `/ui/atoms/${component.toLowerCase()}`,
    element: <Component />,
  };
});

// UT Routes
const utRoutes = Object.keys(utComponents).map((component) => {
  const Component = utComponents[component];
  return {
    path: `/ut/${component.toLowerCase()}`,
    element: <Component />,
  };
});

// - - - - - - - - - - - - - - - - -

const defaultRoute = {
  path: "/",
  element: <Top />,
};

/** Old bookmark `/top` — must be before `/:username` or "top" is treated as a profile slug. */
const topRedirectRoute = {
  path: "/top",
  element: <Navigate to="/" replace />,
};

const confirmEmailChangeRoutes = [
  {
    path: "/auth/confirm-email-change/:token",
    element: <ConfirmEmailChange />,
  },
  {
    path: "/confirm-email-change/:token",
    element: <ConfirmEmailChange />,
  },
  {
    path: "/confirm-email-change",
    element: <ConfirmEmailChange />,
  },
];

/**
 * Password reset page. Use `/confirm-password-reset/:token` in emails — NOT under `/auth/`,
 * or the CRA dev proxy forwards `/auth/*` to PocketBase and you get JSON 404.
 */
const passwordResetRoutes = [
  {
    path: "/confirm-password-reset/:token",
    element: <ConfirmPasswordReset />,
  },
  {
    path: "/auth/confirm-password-reset/:token",
    element: <ConfirmPasswordReset />,
  },
  {
    path: "/recreate-password/:token",
    element: <ConfirmPasswordReset />,
  },
];

/**
 * Signup email confirm — same idea as /confirm-password-reset (SPA path, not under /signup-request).
 * Keep /signup-request/verify for older emails until they expire.
 */
const confirmSignupRoutes = [
  {
    path: "/confirm-signup",
    element: <SignupRequestVerify />,
  },
  {
    path: "/signup-request/verify",
    element: <SignupRequestVerify />,
  },
];

/** Signup / resend verification — NOT under `/auth/` so CRA dev proxy does not forward to PB. */
const confirmVerificationRoutes = [
  {
    path: "/confirm-verification/:token",
    element: <ConfirmVerification />,
  },
  {
    path: "/auth/confirm-verification/:token",
    element: <ConfirmVerification />,
  },
];

/**
 * Old PHP URLs — must be before `/:username` or paths like `/index.php` are treated as profiles.
 */
const legacyPhpRedirectRoutes = [
  { path: "/index.php", element: <Navigate to="/" replace /> },
  { path: "/resos.php", element: <Navigate to="/resos" replace /> },
  { path: "/tools.php", element: <Navigate to="/tools" replace /> },
  { path: "/ideaz.php", element: <Navigate to="/ideaz" replace /> },
  { path: "/apps.php", element: <Navigate to="/apps" replace /> },
];

/** Before `/:username` so `/camp/...` is not mistaken for a profile slug. */
const campEventRoute = {
  path: "/camp/event",
  element: <EventTemp />,
};

const campEventPrivacyRoute = {
  path: "/camp/event/privacy",
  element: <CampEventPrivacyPolicy />,
};

/** Must be last: catches `/username` without stealing `/apps`, `/setting`, etc. */
const profileByUsernameRoute = {
  path: "/:username",
  element: <Profile />,
};

/** After `/:username`: unknown multi-segment paths and anything else unmatched. */
const notFoundRoute = {
  path: "*",
  element: <NotFound />,
};

export default [
  defaultRoute,
  ...confirmEmailChangeRoutes,
  ...passwordResetRoutes,
  ...confirmVerificationRoutes,
  ...confirmSignupRoutes,
  topRedirectRoute,
  ...pageRoutes,
  ...uiRoutes,
  ...utRoutes,
  ...legacyPhpRedirectRoutes,
  campEventRoute,
  campEventPrivacyRoute,
  profileByUsernameRoute,
  notFoundRoute,
];
